import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  AlertCircle, Calendar, TrendingUp, TrendingDown, DollarSign,
  Wallet, Sprout, Wheat, Heart, GraduationCap, Users, Landmark,
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';
import { format, startOfWeek, endOfWeek, parseISO, isWithinInterval, differenceInDays } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role-specific widget: Loan Officer
const LoanOfficerWidgets = () => {
  const [data, setData] = useState({ loans: [], repayments: [] });
  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/loans`),
      axios.get(`${API_URL}/api/repayments`),
    ]).then(([l, r]) => setData({ loans: l.data, repayments: r.data })).catch(() => {});
  }, []);

  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(today, { weekStartsOn: 1 });

  // Overdue: active loans whose issue_date + duration_months is in the past AND has remaining balance
  const overdue = data.loans.filter((l) => {
    if (l.status === 'repaid' || !l.remaining_balance || l.remaining_balance <= 0) return false;
    try {
      const issue = parseISO(l.issue_date);
      const dueDays = (l.duration_months || 6) * 30;
      const daysSince = differenceInDays(today, issue);
      return daysSince > dueDays;
    } catch { return false; }
  });

  // Weekly repayments
  const weekRepayments = data.repayments.filter((r) => {
    try {
      return isWithinInterval(parseISO(r.date), { start: weekStart, end: weekEnd });
    } catch { return false; }
  });
  const weekTotal = weekRepayments.reduce((s, r) => s + r.amount, 0);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="loan-officer-widgets">
      <Card className="card-hover border-red-200 dark:border-red-900/30">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/20"><AlertCircle className="h-4 w-4 text-red-600" /></div>
              <CardTitle className="text-base font-heading">Overdue Loans</CardTitle>
            </div>
            <Badge className="bg-red-100 text-red-700">{overdue.length}</Badge>
          </div>
          <CardDescription>Loans past their due date with remaining balance</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {overdue.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No overdue loans</p>
          ) : overdue.slice(0, 5).map((l) => (
            <div key={l.id} className="flex items-center justify-between text-sm p-2 rounded-lg bg-red-50 dark:bg-red-900/10">
              <div>
                <p className="font-medium">{l.borrower_name}</p>
                <p className="text-xs text-muted-foreground">{l.business_type} · {l.borrower_phone}</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-red-600">{formatCurrency(l.remaining_balance)}</p>
                <p className="text-xs text-muted-foreground">outstanding</p>
              </div>
            </div>
          ))}
          <Link to="/loans" className="text-xs text-primary hover:underline">View all loans →</Link>
        </CardContent>
      </Card>

      <Card className="card-hover border-green-200 dark:border-green-900/30">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/20"><Calendar className="h-4 w-4 text-green-600" /></div>
              <CardTitle className="text-base font-heading">This Week's Repayments</CardTitle>
            </div>
            <Badge className="bg-green-100 text-green-700">{weekRepayments.length}</Badge>
          </div>
          <CardDescription>{format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-3xl font-heading font-bold text-green-600">{formatCurrency(weekTotal)}</p>
          <p className="text-xs text-muted-foreground mt-1">Total collected this week</p>
          {weekRepayments.slice(0, 3).map((r) => (
            <div key={r.id} className="flex justify-between text-sm mt-2 pt-2 border-t">
              <span className="text-muted-foreground">{r.date}</span>
              <span className="font-medium">{formatCurrency(r.amount)}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// Role-specific widget: Finance Officer
const FinanceOfficerWidgets = () => {
  const [data, setData] = useState({ income: [], expenses: [] });
  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/income`),
      axios.get(`${API_URL}/api/expenses`),
    ]).then(([i, e]) => setData({ income: i.data, expenses: e.data })).catch(() => {});
  }, []);

  // Cash flow last 30 days
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const recentIncome = data.income.filter((i) => { try { return parseISO(i.date) >= thirtyDaysAgo; } catch { return false; } });
  const recentExpenses = data.expenses.filter((e) => { try { return parseISO(e.date) >= thirtyDaysAgo; } catch { return false; } });
  const totalIn = recentIncome.reduce((s, i) => s + i.amount, 0);
  const totalOut = recentExpenses.reduce((s, e) => s + e.amount, 0);
  const netFlow = totalIn - totalOut;

  // Top 5 expenses
  const topExpenses = [...data.expenses].sort((a, b) => b.amount - a.amount).slice(0, 5);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="finance-officer-widgets">
      <Card className="card-hover border-primary/20">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10"><TrendingUp className="h-4 w-4 text-primary" /></div>
            <CardTitle className="text-base font-heading">30-Day Cash Flow</CardTitle>
          </div>
          <CardDescription>Income vs Expenses last 30 days</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 dark:bg-green-900/10">
            <span className="text-sm">Income in</span>
            <span className="font-heading font-bold text-green-600">{formatCurrency(totalIn)}</span>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 dark:bg-red-900/10">
            <span className="text-sm">Expenses out</span>
            <span className="font-heading font-bold text-red-600">{formatCurrency(totalOut)}</span>
          </div>
          <div className={`flex items-center justify-between p-3 rounded-lg ${netFlow >= 0 ? 'bg-primary/5' : 'bg-red-50 dark:bg-red-900/10'}`}>
            <span className="text-sm font-medium">Net Cash Flow</span>
            <span className={`font-heading font-bold ${netFlow >= 0 ? 'text-primary' : 'text-red-600'}`}>{formatCurrency(netFlow)}</span>
          </div>
        </CardContent>
      </Card>

      <Card className="card-hover border-red-200 dark:border-red-900/30">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/20"><TrendingDown className="h-4 w-4 text-red-600" /></div>
            <CardTitle className="text-base font-heading">Top 5 Expenses</CardTitle>
          </div>
          <CardDescription>Largest expense entries</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {topExpenses.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No expenses recorded</p>
          ) : topExpenses.map((e, idx) => (
            <div key={e.id} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-xs text-muted-foreground w-4">{idx + 1}.</span>
                <div className="min-w-0 flex-1">
                  <p className="capitalize truncate">{e.category?.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-muted-foreground truncate">{e.description || e.date}</p>
                </div>
              </div>
              <span className="font-bold text-red-600 flex-shrink-0 ml-2">{formatCurrency(e.amount)}</span>
            </div>
          ))}
          <Link to="/finance/expenses" className="text-xs text-primary hover:underline block pt-2">View all expenses →</Link>
        </CardContent>
      </Card>
    </div>
  );
};

// Role-specific widget: Project Manager
const ProjectManagerWidgets = () => {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/projects/summary/all`).then(r => setSummary(r.data)).catch(() => {});
  }, []);
  if (!summary) return null;
  const programs = [
    { key: 'loans_program', label: 'Loans', icon: Landmark, color: 'text-blue-600', path: '/loans' },
    { key: 'maize_program', label: 'Maize', icon: Wheat, color: 'text-amber-600', path: '/maize' },
    { key: 'child_sponsorship', label: 'Sponsorship', icon: Heart, color: 'text-pink-600', path: '/sponsorship' },
    { key: 'vocational_training', label: 'Training', icon: GraduationCap, color: 'text-indigo-600', path: '/training' },
  ];
  return (
    <Card className="border-primary/20" data-testid="project-manager-widgets">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-primary/10"><Sprout className="h-4 w-4 text-primary" /></div>
          <CardTitle className="text-base font-heading">Program Performance</CardTitle>
        </div>
        <CardDescription>Revenue, expenses and net income by program</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {programs.map((p) => {
            const d = summary[p.key] || { revenue: 0, expenses: 0, net_income: 0 };
            const Icon = p.icon;
            return (
              <Link to={p.path} key={p.key} className="p-3 rounded-xl border hover:shadow-md transition-all">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`h-4 w-4 ${p.color}`} />
                  <span className="text-sm font-medium">{p.label}</span>
                </div>
                <p className="text-xs text-muted-foreground">Revenue</p>
                <p className="font-bold text-green-600 text-sm">{formatCurrency(d.revenue)}</p>
                <p className="text-xs text-muted-foreground mt-1">Net</p>
                <p className={`font-bold text-sm ${d.net_income >= 0 ? 'text-primary' : 'text-red-600'}`}>{formatCurrency(d.net_income)}</p>
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// Role-specific widget: School Head
const SchoolHeadWidgets = () => {
  const [data, setData] = useState({ students: null, staff: null, attendance: null });
  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/personnel/students/summary`),
      axios.get(`${API_URL}/api/personnel/staff/summary`),
      axios.get(`${API_URL}/api/personnel/students/attendance-aggregate`).catch(() => ({ data: null })),
    ]).then(([s, st, a]) => setData({ students: s.data, staff: st.data, attendance: a.data })).catch(() => {});
  }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="school-head-widgets">
      <Card className="card-hover border-blue-200">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/20"><Users className="h-4 w-4 text-blue-600" /></div>
            <CardTitle className="text-base font-heading">Students</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-3xl font-heading font-bold">{data.students?.total || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Primary: {data.students?.by_school?.suubi_primary || 0} · Secondary: {data.students?.by_school?.suubi_secondary || 0}
          </p>
        </CardContent>
      </Card>
      <Card className="card-hover border-primary/20">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10"><Calendar className="h-4 w-4 text-primary" /></div>
            <CardTitle className="text-base font-heading">Attendance Rate</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-3xl font-heading font-bold text-primary">{data.attendance?.overall_attendance_rate || 0}%</p>
          <Progress value={data.attendance?.overall_attendance_rate || 0} className="mt-2 h-1.5" />
          <p className="text-xs text-muted-foreground mt-1">{data.attendance?.total_attendance_records || 0} records tracked</p>
        </CardContent>
      </Card>
      <Card className="card-hover border-amber-200">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/20"><GraduationCap className="h-4 w-4 text-amber-600" /></div>
            <CardTitle className="text-base font-heading">Staff</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-3xl font-heading font-bold">{data.staff?.total || 0}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Teaching: {data.staff?.by_category?.teaching || 0} · Admin: {data.staff?.by_category?.administrator || 0}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const RoleWidgets = ({ user }) => {
  const role = user?.role;
  return (
    <div className="space-y-4">
      {role === 'loan_officer' && <LoanOfficerWidgets />}
      {role === 'finance_officer' && <FinanceOfficerWidgets />}
      {role === 'project_manager' && <ProjectManagerWidgets />}
      {role === 'school_head' && <SchoolHeadWidgets />}
      {role === 'admin' && (
        <>
          <LoanOfficerWidgets />
          <FinanceOfficerWidgets />
        </>
      )}
    </div>
  );
};

export default RoleWidgets;
